import { useCallback } from "react";
import { parseAsString, useQueryState } from "nuqs";

interface ParamNavigationOptions {
    replace?: boolean;
}

interface UseUrlParamOptions<T = string> {
    validator?: (rawValue: string) => T | undefined;
}

/**
 * Один query-параметр URL через `nuqs` (с опциональной валидацией).
 */
export function useUrlParam<T = string>(paramName: string, options: UseUrlParamOptions<T> = {}) {
    const { validator } = options;
    const [rawValue, setRawValue] = useQueryState(paramName, parseAsString.withOptions({ history: "push" }));

    const getValidatedValue = useCallback((): T | null => {
        if (rawValue === null) {
            return null;
        }
        if (validator != null) {
            const validated = validator(rawValue);
            return validated ?? null;
        }
        return rawValue as unknown as T;
    }, [rawValue, validator]);

    const setValue = useCallback(
        (value: string | null | undefined, navOptions: ParamNavigationOptions = {}) => {
            void setRawValue(value ?? null, {
                history: navOptions.replace === true ? "replace" : "push",
            });
        },
        [setRawValue],
    );

    const removeValue = useCallback(
        (navOptions: ParamNavigationOptions = {}) => {
            setValue(null, navOptions);
        },
        [setValue],
    );

    return {
        value: getValidatedValue(),
        rawValue,
        setValue,
        removeValue,
    };
}
